/**
 * Formatting an instant the server gave us.
 *
 * THE CLOCK BAN DOES NOT APPLY HERE AND THAT IS EXPLICIT. `no-restricted-syntax` in the root ESLint
 * config bans `Date.now()`, `new Date()` and `performance.now()` everywhere, and its own message
 * says: "Parsing an instant you were given — new Date(value) — is allowed and is not a clock read."
 * Every call below parses a `ServerTime` that arrived in a payload. Nothing here reads the current
 * time, and nothing here computes "how long ago" — a relative time would be a clock read.
 *
 * THE TIME ZONE IS STATED IN TEXT. docs/ui/03-components.md requires a date and time to carry
 * its zone in words rather than leaving the reader to guess. `ServerTime` is an instant; rendering
 * it in the browser's local zone without saying so would make two authors reading the same terms
 * page disagree about the effective date. So it is rendered in UTC and says UTC.
 */
// ★ THE CABINET'S LANGUAGE (design 24, T16; design 29, W5): `CABINET_LOCALE` follows it, so a
// formatter is built per call - one built at load would keep the language of that moment. These two
// once wrote British English ("1 January 2026") while the rest of the cabinet wrote American.
import { CABINET_LOCALE } from '../cabinet/format';

const DAY: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
};

const DAY_AND_TIME: Intl.DateTimeFormatOptions = {
  ...DAY,
  hour: '2-digit',
  minute: '2-digit',
  timeZoneName: 'short',
};

function parse(serverTime: string): Date | null {
  const parsed = new Date(serverTime);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * "Jan 1, 2026". Returns the raw string when it will not parse — showing the author what the
 * server actually sent beats showing "Invalid Date", and beats hiding the field.
 */
export function formatDay(serverTime: string): string {
  const parsed = parse(serverTime);
  return parsed === null ? serverTime : new Intl.DateTimeFormat(CABINET_LOCALE, DAY).format(parsed);
}

/** "Feb 11, 2026, 09:14 AM UTC". Used where the exact moment is the point, as on an acceptance. */
export function formatDayAndTime(serverTime: string): string {
  const parsed = parse(serverTime);
  return parsed === null
    ? serverTime
    : new Intl.DateTimeFormat(CABINET_LOCALE, DAY_AND_TIME).format(parsed);
}
